import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Form } from 'semantic-ui-react';
import moment from 'moment';
import { useMessageResources, useTextResources } from '../../language';
import { DatePicker } from '../../components/shared/DatePicker';
import { dateFormat, dateTimeFormat, MaintenanceStatusTypes, MaintenanceStatusTypeEnum } from '../../components/shared/constants';
import GridSearchTemplate from '../GridSearchTemplate';
import RoomMaintenancePopup from '../../components/RoomMaintenancePopup';
import { RoomPlannedMaintenance as RoomPlannedMaintenanceService, Rooms } from '../../api/services/next';
import RoomsDropDown from '../../components/shared/RoomsDropDown';
import HotelsDropDown from '../../components/shared/HotelsDropDown';
import DropDown from '../../components/shared/dropdown';
import { useCurrentHotel } from '../../hooks/useCurrentHotel';
import { onError, onSuccess } from '../../components/shared/messages';
import { useLoader } from '../../hooks/useLoader';
import { useConfirm } from '../../hooks/useConfirm';
import { EditButton, TrashButton } from '../../components/shared/icons';
import { PermissionsActions, UserHasPermission } from '../../components/shared/permissionsActions';

// Wrapper component that bridges PopupEditing interface to RoomMaintenancePopup interface
const RoomMaintenancePopupWrapper = ({ open, onCancelChanges, extendedProps: { addRow, currentHotelId } = {} }) => {
    const currentBusinessDate = window.$user?.CurrentHotel?.HotelParameter?.CurrentBusinessDate || new Date();
    const endDate = moment(currentBusinessDate).add(1, 'days').toDate();

    const defaultMaintenance = useMemo(() => ({
        Id: 0,
        Description: '',
        StartDate: currentBusinessDate,
        EndDate: endDate,
        RoomNumber: null,
        CreateDate: null,
        IsActive: true,
        MaintenanceStatusTypeId: null,
        MaintenanceStatusType: null,
        Room: null,
        RoomId: null,
        HotelId: currentHotelId,
    }), [currentHotelId]);

    const handleClose = useCallback(() => {
        onCancelChanges?.();
    }, [onCancelChanges]);

    const handleSave = useCallback(async () => {
        await addRow?.();
    }, [addRow]);

    return (
        <RoomMaintenancePopup
            open={open}
            onClose={handleClose}
            maintenance={defaultMaintenance}
            onSave={handleSave}
        />
    );
};

const RoomPlannedMaintenance = ({ HotelId = null }) => {
    const text = useTextResources();
    const message = useMessageResources();
    const loader = useLoader();
    const confirm = useConfirm();
    const currentHotel = useCurrentHotel();
    const [gridData, setGridData] = useState([]);
    const [showPopup, setShowPopup] = useState(false);
    const [selectedMaintenance, setSelectedMaintenance] = useState(null);
    const [currentSearchSpec, setCurrentSearchSpec] = useState({});
    const [roomsMap, setRoomsMap] = useState({});
    const [hotelIds, setHotelIds] = useState(HotelId ? [HotelId] : (currentHotel?.Id ? [currentHotel.Id] : []));
    const [datesRange, setDatesRange] = useState(null);
    const [selected, setSelected] = useState([]);
    const doSearch = useRef();

    // Refresh data function - calls API with current search spec
    const refreshData = useCallback(async () => {
        try {
            loader(true);
            // Build the API spec from current search spec - use hotelIds
            const apiSpec = {
                HotelIDs: hotelIds?.length ? hotelIds : (currentHotel?.Id ? [currentHotel.Id] : null),
                IsActive: true,
                GetPlannedMaintenanceStatusesContainedInRange: true
            };

            // Add date filters if provided (from date range)
            if (datesRange && datesRange.length === 2) {
                apiSpec.FromDate = datesRange[0];
                apiSpec.ToDate = datesRange[1];
            }

            // Add maintenance status filter
            if (currentSearchSpec.MaintenanceStatusTypeId) {
                apiSpec.MaintenanceStatusTypeId = currentSearchSpec.MaintenanceStatusTypeId;
            }

            // Add room filter
            if (currentSearchSpec.RoomId) {
                apiSpec.RoomId = currentSearchSpec.RoomId;
            }

            // Add includes to get user names
            apiSpec.includes = ['Users'];
            
            const data = await RoomPlannedMaintenanceService.GetRoomPlannedMaintenances(apiSpec);
            
            // Enrich data with room information
            const enrichedData = (data || []).map(item => {
                const room = roomsMap[item.RoomId];
                return {
                    ...item,
                    RoomNumber: room?.Number || '',
                    RoomName: room?.Name || ''
                };
            });
            setGridData(enrichedData);
        } catch (error) {
            console.error('Error refreshing data:', error);
            onError(error);
        } finally {
            loader(false);
        }
    }, [hotelIds, currentHotel?.Id, currentSearchSpec, roomsMap, loader, datesRange]);

    // Fetch rooms on hotel change
    useEffect(() => {
        const fetchRooms = async () => {
            // Determine which hotel IDs to use
            const effectiveHotelIds = hotelIds?.length ? hotelIds : (currentHotel?.Id ? [currentHotel.Id] : []);
            
            if (effectiveHotelIds.length) {
                try {
                    // Fetch rooms for all selected hotels
                    const allRooms = [];
                    for (const hotelId of effectiveHotelIds) {
                        const rooms = await Rooms.GetRooms({ HotelId: hotelId });
                        allRooms.push(...(rooms || []));
                    }
                    // Create a lookup map by RoomId
                    const map = {};
                    allRooms.forEach(room => {
                        map[room.Id] = room;
                    });
                    setRoomsMap(map);
                } catch (error) {
                    console.error('Error fetching rooms:', error);
                }
            } else {
                setRoomsMap({});
            }
        };
        fetchRooms();
    }, [hotelIds, currentHotel?.Id]);

    // Maintenance status options for dropdown (including "All" option)
    const maintenanceStatusOptions = useMemo(() => [
        { Id: 0, Name: text('All') },
        ...MaintenanceStatusTypes.map(type => ({
            Id: type.Id,
            Name: text(type.Name)
        }))
    ], [text]);

    // Refresh grid by re-triggering search (used by modalEditor after save)
    const addRow = () => {
        doSearch.current?.();
    };

    // Permission check for delete - matches RoomMaintenancePopup pattern
    const canDelete = useCallback((typeId) => {
        if (typeId === 2) return UserHasPermission(PermissionsActions.DeleteOOO);
        if (typeId === 1) return UserHasPermission(PermissionsActions.DeleteOOS);
        return false;
    }, []);

    // Handle edit - called from actions column
    const handleEdit = useCallback((item) => {
        setSelectedMaintenance({
            ...item,
            HotelId: currentHotel?.Id
        });
        setShowPopup(true);
    }, [currentHotel?.Id]);

    // Handle delete with confirmation modal - called from actions column
    const handleDelete = useCallback(async (item) => {
        if (!canDelete(item.MaintenanceStatusTypeId)) {
            onError(text('NoPermission'));
            return;
        }
        await confirm({
            header: `${text('Delete')} ${text('Maintenance')}`,
            content: message('WouldYouLikeToContinue'),
            confirmButton: {
                content: text('Yes'),
                positive: true,
            },
            cancelButton: {
                content: text('No'),
                negative: true,
            },
            onConfirm: async () => {
                try {
                    loader(true);
                    const deletedItem = { ...item, IsActive: false };
                    await RoomPlannedMaintenanceService.UpdateRoomPlannedMaintenances([deletedItem]);
                    onSuccess('DeletedSuccessfully');
                    // Refresh data after delete
                    await refreshData();
                } catch (error) {
                    console.error('Error deleting maintenance:', error);
                    onError(error);
                } finally {
                    loader(false);
                }
            }
        });
    }, [confirm, text, loader, refreshData]);

    // Actions formatter - renders Edit and Delete buttons
    const actionsFormatter = useCallback(({ row }) => {
        if (!row) return null;
        return (
            <>
                <EditButton
                    content="Edit"
                    onClick={() => handleEdit(row)}
                    style={{ marginRight: '0.5rem' }}
                />
                <TrashButton
                    content="Delete"
                    onClick={() => handleDelete(row)}
                />
            </>
        );
    }, [handleEdit, handleDelete]);

    // Confirm deletion dialog (used by multiple delete)
    const confirmDeletion = useCallback(() => confirm({
        header: `${text('Delete')} ${text('Maintenance')}`,
        content: message('AreYouSureYouWouldLikeToDeleteSelectedItems'),
        confirmButton: {
            content: text('Yes'),
            positive: true,
        },
        cancelButton: {
            content: text('No'),
            negative: true,
        },
    }), [confirm, text, message]);

    // Handle grid update (multiple delete via showDeleteMultiple)
    const onGridUpdate = useCallback(async (added, changed, deleted) => {
        if (deleted?.length > 0) {
            const itemsToDelete = deleted.map(index => gridData[index]).filter(Boolean);
            // Check permissions for each item
            const unauthorizedItems = itemsToDelete.filter(item => !canDelete(item.MaintenanceStatusTypeId));
            if (unauthorizedItems.length > 0) {
                onError(text('NoPermission'));
                return;
            }
            if (await confirmDeletion()) {
                try {
                    loader(true);
                    const deletedItems = itemsToDelete.map(item => ({ ...item, IsActive: false }));
                    await RoomPlannedMaintenanceService.UpdateRoomPlannedMaintenances(deletedItems);
                    onSuccess('DeletedSuccessfully');
                    await refreshData();
                } catch (error) {
                    console.error('Error deleting maintenance items:', error);
                    onError(error);
                } finally {
                    loader(false);
                }
            }
        }
    }, [confirmDeletion, gridData, loader, refreshData, canDelete, text]);

    // Handle popup close
    const handlePopupClose = useCallback(async (shouldRefresh) => {
        setShowPopup(false);
        setSelectedMaintenance(null);
        if (shouldRefresh) {
            // Refresh data after popup close
            await refreshData();
        }
    }, [refreshData]);

    // Handle popup save
    const handlePopupSave = useCallback(async (savedItems) => {
        // Refresh data after save
        await refreshData();
    }, [refreshData]);

    // Handle search state change - store the current search spec for refresh
    const onSearchChange = (newSpec) => {
        setCurrentSearchSpec(newSpec);
    };

    // Handle search complete - enrich data with room info
    const onSearchComplete = useCallback((data) => {
        // Enrich data with room information from the lookup map
        const enrichedData = (data || []).map(item => {
            const room = roomsMap[item.RoomId];
            return {
                ...item,
                RoomNumber: room?.Number || '',
                RoomName: room?.Name || ''
            };
        });
        setGridData(enrichedData);
    }, [roomsMap]);

    // Date formatter
    const dateFormatter = ({ value }) => {
        return value ? moment(value).format(dateFormat) : '';
    };

    // DateTime formatter
    const dateTimeFormatter = ({ value }) => {
        return value ? moment(value).format(dateTimeFormat) : '';
    };

    // Room formatter - display room number using the lookup map
    const roomFormatter = ({ value, row }) => {
        // First check if we already enriched the data
        if (row?.RoomNumber) {
            return row.RoomNumber;
        }
        // Fallback to lookup map
        if (row?.RoomId && roomsMap[row.RoomId]) {
            return roomsMap[row.RoomId].Number;
        }
        return '';
    };

    // Maintenance status type formatter
    const maintenanceStatusFormatter = ({ value, row }) => {
        if (row?.MaintenanceStatusTypeId) {
            const statusType = MaintenanceStatusTypes.find(t => t.Id === row.MaintenanceStatusTypeId);
            return statusType ? text(statusType.Name) : '';
        }
        return '';
    };

    // User formatter - display username from UpdatedByUserName (populated by API with Users include)
    const userFormatter = ({ value, row }) => {
        if (row?.UpdatedByUserName) {
            return row.UpdatedByUserName;
        }
        return row?.UpdatedByUserId ? `User ${row.UpdatedByUserId}` : '';
    };

    // Columns definition
    const columns = useMemo(() => [
        {
            name: 'RoomNumber',
            title: text('Room'),
            formatter: roomFormatter,
            getCellValue: row => {
                if (row?.RoomNumber) return row.RoomNumber;
                if (row?.RoomId && roomsMap[row.RoomId]) return roomsMap[row.RoomId].Number;
                return '';
            }
        },
        {
            name: 'StartDate',
            title: text('StartDate'),
            formatter: dateFormatter
        },
        {
            name: 'EndDate',
            title: text('EndDate'),
            formatter: dateFormatter
        },
        {
            name: 'MaintenanceStatusTypeId',
            title: text('MaintenanceStatusType'),
            formatter: maintenanceStatusFormatter,
            getCellValue: row => {
                if (row?.MaintenanceStatusTypeId) {
                    const statusType = MaintenanceStatusTypes.find(t => t.Id === row.MaintenanceStatusTypeId);
                    return statusType ? text(statusType.Name) : '';
                }
                return '';
            }
        },
        {
            name: 'Description',
            title: text('Description')
        },
        {
            name: 'UpdatedByUserId',
            title: text('User'),
            formatter: userFormatter,
            getCellValue: row => row?.UpdatedByUserName || ''
        },
        {
            name: 'CreateDate',
            title: text('CreateDate'),
            formatter: dateTimeFormatter
        },
        {
            name: 'Actions',
            title: text('Actions'),
            formatter: actionsFormatter,
            getCellValue: row => row,
            exportEnabled: false // Exclude from export
        }
    ], [text, roomsMap, actionsFormatter]);

    // Rooms spec for dropdown - uses hotelIds for filtering
    // Only pass HotelIds if there are selected hotels, otherwise undefined
    const roomsSpec = useMemo(() => {
        if (hotelIds?.length) {
            return { HotelIds: hotelIds };
        }
        return { HotelIds: undefined };
    }, [hotelIds]);

    // Grid options configuration following Allocation pattern
    const gridOptions = useMemo(() => {
        const gridConfig = {
            columns: columns,
            cacheId: 'RoomPlannedMaintenance',
            export: true,
            excel: {
                hiddenColumns: [{ columnName: 'Actions' }] // Exclude Actions column from Excel export
            },
            customOptions: {
                sort: {
                    sorting: [
                        { columnName: 'StartDate', direction: 'desc' }
                    ]
                },
                order: {},
                hidden: {
                    hiddenColumns: []
                }
            },
            showFilter: true,
            noVirtualTable: true,
            paging: {
                pageSize: 10
            },
            filtering: {
                extensions: [
                    { columnName: 'Actions', filteringEnabled: false }
                ]
            },
            summary: true,
            groupSummaryItems: [
                { columnName: 'Actions', type: 'max', alignByColumn: true, formatter: actionsFormatter, hideTypeName: true },
            ],
            selectable: true,
            showDeleteMultiple: true,
            editable: {
                allowAdd: true,
                modalEditor: {
                    modal: RoomMaintenancePopupWrapper,
                    props: {
                        addRow: addRow,
                        currentHotelId: currentHotel?.Id,
                    }
                }
            }
        };

        return {
            search: {
                fields: [
                    <Form.Field
                        control={HotelsDropDown}
                        multiple
                        clearable
                        search
                        label={text("Hotels")}
                        key="HotelIds"
                        onChange={setHotelIds}
                    />,
                    <DatePicker
                        picktype='range'
                        label={text('Dates')}
                        key='DatesRange'
                        onChange={setDatesRange}
                        clearable
                    />,
                    <Form.Field
                        control={DropDown}
                        data={maintenanceStatusOptions}
                        textField="Name"
                        valueField="Id"
                        clearable
                        label={text("MaintenanceStatus")}
                        key="MaintenanceStatusTypeId"
                    />,
                    <Form.Field
                        control={RoomsDropDown}
                        spec={roomsSpec}
                        clearable
                        search
                        label={text("Room")}
                        key="RoomId"
                    />
                ],
                method: 'GET',
                api: async (searchSpec) => {
                    // Build the API spec - use hotelIds from state
                    const apiSpec = {
                        HotelIDs: hotelIds?.length ? hotelIds : (currentHotel?.Id ? [currentHotel.Id] : null),
                        IsActive: true,
                        GetPlannedMaintenanceStatusesContainedInRange: true
                    };

                    // Add date filters if provided (from date range)
                    if (datesRange && datesRange.length === 2) {
                        apiSpec.FromDate = datesRange[0];
                        apiSpec.ToDate = datesRange[1];
                    }

                    // Add maintenance status filter
                    if (searchSpec.MaintenanceStatusTypeId) {
                        apiSpec.MaintenanceStatusTypeId = searchSpec.MaintenanceStatusTypeId;
                    }

                    // Add room filter
                    if (searchSpec.RoomId) {
                        apiSpec.RoomId = searchSpec.RoomId;
                    }

                    // Add includes to get user names
                    apiSpec.includes = ['Users'];

                    return await RoomPlannedMaintenanceService.GetRoomPlannedMaintenances(apiSpec);
                },
                setDoSearchRef: ref => doSearch.current = ref,
                searchOnLoad: true,
                initial: {
                    HotelIds: hotelIds?.length ? hotelIds : null,
                    DatesRange: null,
                    MaintenanceStatusTypeId: 0,
                    RoomId: null
                }
            },
            grid: gridConfig
        };
    }, [text, columns, hotelIds, currentHotel?.Id, maintenanceStatusOptions, roomsSpec, datesRange, actionsFormatter]);

    return (
        <div className="gridSearchTemplate">
            <GridSearchTemplate
                options={gridOptions}
                onGridUpdate={onGridUpdate}
                onSelected={setSelected}
                onSearchChange={onSearchChange}
                onSearchComplete={onSearchComplete}
                data={gridData}
            />

            <RoomMaintenancePopup
                open={showPopup}
                onClose={() => handlePopupClose(true)}
                maintenance={selectedMaintenance}
                onSave={handlePopupSave}
            />
        </div>
    );
};

export default RoomPlannedMaintenance;